import { useMemo } from "react";
import * as THREE from "three";
import { makeHackerPosterTexture, makeSecurityFeedTexture } from "./textTexture";

/**
 * The attacker's den — reused across courses (deepfake studio, ransomware leak,
 * etc.). Zero external assets: pure geometry + CSP-safe canvas textures. Lit by
 * the cold glow of screens with a red rim for a menacing mood. It must read at a
 * glance as "this is where the hacker operates", so it's dressed with:
 *   - a large "WE HACK THE WORLD" Anonymous poster on the back wall,
 *   - a wall + side cluster of monitors running unrelated security feeds,
 *   - Raspberry-Pi boards and clutter on the desk next to the laptop,
 *   - a drinks trolley piled with coffee mugs and energy-drink cans.
 * Waypoints:
 *   - wp_hacker_establish: wide, dim room (hard CUT in).
 *   - wp_hacker_laptop: push in on the laptop where overlays sit (~[0,0.95,-0.2]).
 */
export function HackerRoom() {
  return (
    <group>
      {/* floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[24, 24]} />
        <meshStandardMaterial color="#16181d" roughness={1} />
      </mesh>
      {/* back + side walls */}
      <mesh position={[0, 2.2, -2.4]} receiveShadow>
        <boxGeometry args={[20, 4.4, 0.2]} />
        <meshStandardMaterial color="#1b1e24" roughness={1} />
      </mesh>
      <mesh position={[-3.6, 2.2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <boxGeometry args={[8, 4.4, 0.2]} />
        <meshStandardMaterial color="#181b21" roughness={1} />
      </mesh>

      {/* desk */}
      <mesh position={[0, 0.72, -0.2]} castShadow receiveShadow>
        <boxGeometry args={[2.6, 0.06, 1.1]} />
        <meshStandardMaterial color="#23262c" roughness={0.6} metalness={0.2} />
      </mesh>
      {[
        [-1.2, 0.36, 0.2],
        [1.2, 0.36, 0.2],
        [-1.2, 0.36, -0.6],
        [1.2, 0.36, -0.6],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.06, 0.72, 0.06]} />
          <meshStandardMaterial color="#0c0e11" metalness={0.4} roughness={0.5} />
        </mesh>
      ))}

      <Laptop />
      <SideMonitor />
      <DeskClutter />
      <HackerPoster />
      <MonitorWall />
      <SideMonitorCluster />
      <RaspberryPis />
      <DrinksTrolley />

      {/* cold screen fill light + red rim + a soft wash so the dressing reads */}
      <pointLight position={[0, 1.35, 0.75]} intensity={1.15} distance={5} color="#7fd8ff" />
      <pointLight position={[1.6, 1.2, -0.4]} intensity={1.6} distance={4} color="#ff3b50" />
      <pointLight position={[0, 3.2, 1.5]} intensity={0.3} distance={9} color="#3a4a6a" />
      {/* glow that lifts the back wall (poster + monitor bank) out of pure black */}
      <pointLight position={[1.7, 2.2, -1.4]} intensity={0.9} distance={6} color="#39ff9a" />
      <pointLight position={[-2.3, 2.5, -1.5]} intensity={0.55} distance={5} color="#cfe3ff" />
    </group>
  );
}

/** Open laptop, screen glowing — DOM overlays composite on top. */
function Laptop() {
  return (
    <group position={[0, 0.75, -0.2]}>
      <mesh position={[0, 0.01, 0.16]} castShadow receiveShadow>
        <boxGeometry args={[0.62, 0.025, 0.42]} />
        <meshStandardMaterial color="#202329" metalness={0.6} roughness={0.4} />
      </mesh>
      {/* keyboard deck — lies flat (rotated -90deg) on the base. */}
      <mesh position={[0, 0.024, 0.16]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
        <planeGeometry args={[0.56, 0.36]} />
        <meshStandardMaterial color="#15171c" roughness={0.7} />
      </mesh>
      <group position={[0, 0.2, -0.04]} rotation={[-0.32, 0, 0]}>
        <mesh castShadow>
          <boxGeometry args={[0.64, 0.4, 0.02]} />
          <meshStandardMaterial color="#16181d" metalness={0.6} roughness={0.4} />
        </mesh>
        <mesh position={[0, 0, 0.012]}>
          <planeGeometry args={[0.6, 0.36]} />
          <meshStandardMaterial
            color="#163a5a"
            emissive={new THREE.Color("#3f86c4")}
            emissiveIntensity={1.7}
            roughness={0.3}
          />
        </mesh>
      </group>
    </group>
  );
}

function SideMonitor() {
  return (
    <group position={[1.55, 0.95, -0.5]} rotation={[0, -0.5, 0]}>
      <mesh castShadow>
        <boxGeometry args={[0.7, 0.42, 0.03]} />
        <meshStandardMaterial color="#0c0e11" roughness={0.4} />
      </mesh>
      <mesh position={[0, 0, 0.018]}>
        <planeGeometry args={[0.66, 0.38]} />
        <meshStandardMaterial
          color="#1a0608"
          emissive={new THREE.Color("#aa1f2c")}
          emissiveIntensity={0.6}
          roughness={0.4}
        />
      </mesh>
      <mesh position={[0, -0.3, 0]} castShadow>
        <boxGeometry args={[0.1, 0.2, 0.06]} />
        <meshStandardMaterial color="#0c0e11" />
      </mesh>
    </group>
  );
}

/** Large "WE HACK THE WORLD" Anonymous poster on the back wall (left of desk). */
function HackerPoster() {
  const tex = useMemo(() => makeHackerPosterTexture(), []);
  return (
    <group position={[-2.35, 2.35, -2.28]}>
      {/* thin frame */}
      <mesh position={[0, 0, -0.01]}>
        <planeGeometry args={[1.62, 2.12]} />
        <meshStandardMaterial color="#0a0c10" roughness={0.9} />
      </mesh>
      <mesh>
        <planeGeometry args={[1.5, 2.0]} />
        <meshStandardMaterial
          map={tex}
          emissive={"#ffffff"}
          emissiveMap={tex}
          emissiveIntensity={0.5}
          roughness={0.95}
        />
      </mesh>
    </group>
  );
}

/** Single wall-mounted monitor running a security feed (unlit = always glowing). */
function FeedMonitor({
  position,
  rotation = [0, 0, 0],
  seed,
  w = 0.5,
  h = 0.32,
}: {
  position: [number, number, number];
  rotation?: [number, number, number];
  seed: number;
  w?: number;
  h?: number;
}) {
  const tex = useMemo(() => makeSecurityFeedTexture(seed), [seed]);
  return (
    <group position={position} rotation={rotation}>
      <mesh>
        <boxGeometry args={[w + 0.04, h + 0.04, 0.03]} />
        <meshStandardMaterial color="#0a0c10" roughness={0.5} metalness={0.3} />
      </mesh>
      <mesh position={[0, 0, 0.02]}>
        <planeGeometry args={[w, h]} />
        <meshBasicMaterial map={tex} toneMapped={false} />
      </mesh>
    </group>
  );
}

/** A bank of monitors on the back wall (right of the poster) — "dozens of feeds". */
function MonitorWall() {
  const cols = 5;
  const rows = 3;
  const w = 0.5;
  const h = 0.32;
  const gx = 0.085;
  const gy = 0.1;
  const originX = 0.45;
  const originY = 2.85;
  const items: { pos: [number, number, number]; seed: number }[] = [];
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      items.push({
        pos: [originX + c * (w + gx), originY - r * (h + gy), -2.26],
        seed: r * 10 + c + 1,
      });
    }
  }
  return (
    <group>
      {items.map((it, i) => (
        <FeedMonitor key={i} position={it.pos} seed={it.seed} w={w} h={h} />
      ))}
    </group>
  );
}

/** A second cluster angled on the left side wall, facing the desk. */
function SideMonitorCluster() {
  const w = 0.46;
  const h = 0.3;
  const items: { pos: [number, number, number]; seed: number }[] = [];
  const cols = 2;
  const rows = 3;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      items.push({
        pos: [-3.45, 2.45 - r * (h + 0.1), -0.9 + c * (w + 0.1)],
        seed: 100 + r * 10 + c,
      });
    }
  }
  return (
    <group>
      {items.map((it, i) => (
        <FeedMonitor key={i} position={it.pos} rotation={[0, Math.PI / 2, 0]} seed={it.seed} w={w} h={h} />
      ))}
    </group>
  );
}

/** A couple of bare Raspberry-Pi-style boards on the desk next to the laptop. */
function RaspberryPis() {
  function Board({ position, rotation = 0 }: { position: [number, number, number]; rotation?: number }) {
    return (
      <group position={position} rotation={[0, rotation, 0]}>
        {/* green PCB */}
        <mesh rotation={[-Math.PI / 2, 0, 0]} castShadow>
          <planeGeometry args={[0.13, 0.09]} />
          <meshStandardMaterial color="#0f7a3a" roughness={0.6} metalness={0.2} side={THREE.DoubleSide} />
        </mesh>
        {/* black SoC chip */}
        <mesh position={[0, 0.006, 0]} castShadow>
          <boxGeometry args={[0.03, 0.008, 0.03]} />
          <meshStandardMaterial color="#0a0a0c" roughness={0.4} />
        </mesh>
        {/* silver USB/Ethernet block */}
        <mesh position={[0.045, 0.008, 0.018]} castShadow>
          <boxGeometry args={[0.03, 0.016, 0.04]} />
          <meshStandardMaterial color="#aab2bd" metalness={0.8} roughness={0.35} />
        </mesh>
        {/* GPIO header */}
        <mesh position={[-0.04, 0.006, -0.03]}>
          <boxGeometry args={[0.05, 0.01, 0.008]} />
          <meshStandardMaterial color="#1a1a1a" roughness={0.5} />
        </mesh>
        {/* status LED */}
        <mesh position={[0.05, 0.007, -0.03]}>
          <boxGeometry args={[0.006, 0.006, 0.006]} />
          <meshStandardMaterial color="#39ff9a" emissive={new THREE.Color("#39ff9a")} emissiveIntensity={2} />
        </mesh>
      </group>
    );
  }
  return (
    <group>
      <Board position={[0.62, 0.757, -0.32]} rotation={0.3} />
      <Board position={[0.82, 0.757, -0.18]} rotation={-0.5} />
      {/* a thin ribbon/jumper wire between them */}
      <mesh position={[0.72, 0.758, -0.25]} rotation={[Math.PI / 2, 0, 0.7]}>
        <torusGeometry args={[0.05, 0.004, 6, 20]} />
        <meshStandardMaterial color="#c23b3b" roughness={0.6} />
      </mesh>
    </group>
  );
}

/** Small side table piled with coffee mugs and energy-drink cans. */
function DrinksTrolley() {
  function Mug({ position, color }: { position: [number, number, number]; color: string }) {
    return (
      <group position={position}>
        <mesh castShadow>
          <cylinderGeometry args={[0.045, 0.04, 0.095, 16]} />
          <meshStandardMaterial color={color} roughness={0.55} />
        </mesh>
        {/* handle */}
        <mesh position={[0.05, 0, 0]} rotation={[Math.PI / 2, 0, 0]}>
          <torusGeometry args={[0.025, 0.007, 8, 16]} />
          <meshStandardMaterial color={color} roughness={0.55} />
        </mesh>
      </group>
    );
  }
  function Can({ position, color }: { position: [number, number, number]; color: string }) {
    return (
      <mesh position={position} castShadow>
        <cylinderGeometry args={[0.028, 0.028, 0.12, 14]} />
        <meshStandardMaterial color={color} metalness={0.7} roughness={0.3} />
      </mesh>
    );
  }
  const tableX = -2.0;
  const tableZ = 0.5;
  const top = 0.62;
  return (
    <group>
      {/* table top + legs */}
      <mesh position={[tableX, top, tableZ]} castShadow receiveShadow>
        <boxGeometry args={[0.7, 0.04, 0.5]} />
        <meshStandardMaterial color="#202329" roughness={0.7} metalness={0.2} />
      </mesh>
      {[
        [tableX - 0.3, top / 2, tableZ - 0.2],
        [tableX + 0.3, top / 2, tableZ - 0.2],
        [tableX - 0.3, top / 2, tableZ + 0.2],
        [tableX + 0.3, top / 2, tableZ + 0.2],
      ].map((p, i) => (
        <mesh key={i} position={p as [number, number, number]} castShadow>
          <boxGeometry args={[0.04, top, 0.04]} />
          <meshStandardMaterial color="#0c0e11" metalness={0.4} roughness={0.5} />
        </mesh>
      ))}
      {/* mugs */}
      <Mug position={[tableX - 0.22, top + 0.07, tableZ - 0.12]} color="#2a2d33" />
      <Mug position={[tableX - 0.05, top + 0.07, tableZ - 0.16]} color="#3a2d2d" />
      <Mug position={[tableX + 0.18, top + 0.07, tableZ - 0.05]} color="#23323a" />
      <Mug position={[tableX - 0.12, top + 0.07, tableZ + 0.12]} color="#2a2d33" />
      {/* energy-drink cans (some toppled) */}
      <Can position={[tableX + 0.12, top + 0.06, tableZ - 0.18]} color="#3fae3f" />
      <Can position={[tableX + 0.24, top + 0.06, tableZ + 0.12]} color="#d23b3b" />
      <Can position={[tableX - 0.02, top + 0.06, tableZ + 0.06]} color="#2f6fd2" />
      <Can position={[tableX + 0.05, top + 0.075, tableZ + 0.2]} color="#c8a020" />
      {/* a toppled can lying on its side */}
      <mesh position={[tableX - 0.25, top + 0.03, tableZ + 0.18]} rotation={[Math.PI / 2, 0, 0.4]} castShadow>
        <cylinderGeometry args={[0.028, 0.028, 0.12, 14]} />
        <meshStandardMaterial color="#3fae3f" metalness={0.7} roughness={0.3} />
      </mesh>
    </group>
  );
}

function DeskClutter() {
  return (
    <group>
      {/* coffee mug */}
      <mesh position={[-0.95, 0.81, 0.1]} castShadow>
        <cylinderGeometry args={[0.05, 0.045, 0.1, 16]} />
        <meshStandardMaterial color="#2a2d33" roughness={0.6} />
      </mesh>
      {/* phone face-down */}
      <mesh position={[-0.7, 0.755, 0.32]} rotation={[0, 0.4, 0]} castShadow>
        <boxGeometry args={[0.08, 0.012, 0.16]} />
        <meshStandardMaterial color="#0a0a0c" roughness={0.4} />
      </mesh>
      {/* sticky notes on the wall */}
      {[
        [-0.5, 1.7, -2.28, "#e7d24a"],
        [-0.2, 1.55, -2.28, "#7fd0e7"],
        [-0.7, 1.45, -2.28, "#e78fb0"],
      ].map((p, i) => (
        <mesh key={i} position={[p[0] as number, p[1] as number, p[2] as number]} rotation={[0, 0, (i - 1) * 0.1]}>
          <planeGeometry args={[0.16, 0.16]} />
          <meshStandardMaterial color={p[3] as string} roughness={0.95} />
        </mesh>
      ))}
      {/* tangled cable on the desk */}
      <mesh position={[0.5, 0.755, 0.35]} rotation={[Math.PI / 2, 0, 0.6]}>
        <torusGeometry args={[0.08, 0.012, 8, 24]} />
        <meshStandardMaterial color="#0c0e11" roughness={0.7} />
      </mesh>
    </group>
  );
}
